import { QueryResolutionDropDownReasons } from "../../../../../constants/dropDownReasons";
import { PaRequired } from "../../../../../data-model/cptCodesTableModel";
import { ReasonModel } from "../../../../../data-model/reasonModel";
import { MedicalPaOrdersAuthStatus } from "../../../../../enums/medicalPaOrdersAuthStatus";
import { ModalId } from "../../../../../enums/modalId";
import { setOpenedModalId } from "../../../../../redux/slice/modalSlice";
import { AppDispatch } from "../../../../../redux/store/store";
import {
  formatReasonDropdownOptions,
  ReasonDropdownOption,
} from "./formatReasonDropdownOptions";

/**
 * Determines whether the Hold modal should be opened based on auth status transition.
 *
 * The Hold modal is opened when the new status is "Hold", except when transitioning
 * from a "Query" status (as Query transitions are handled by the StatusUpdateModal instead).
 *
 * @param oldValue - The previous auth status value before the change
 * @param newValue - The new auth status value being set
 * @returns `true` if the Hold modal should be opened, `false` otherwise
 */
export const shouldOpenHoldModal = (oldValue: any, newValue: any) => {
  if (oldValue === MedicalPaOrdersAuthStatus.Query) {
    return false;
  } else if (newValue === MedicalPaOrdersAuthStatus.Hold) {
    return true;
  } else {
    return false;
  }
};

export const shouldOpenStatusUpdateModal = (oldValue: any, newValue: any) => {
  // 1. check if new status is Query or Denied by RISA
  if (
    newValue === MedicalPaOrdersAuthStatus.Query ||
    newValue === MedicalPaOrdersAuthStatus.DeniedByRISA
  ) {
    return true;
  }

  if (
    oldValue === MedicalPaOrdersAuthStatus.Query &&
    newValue !== MedicalPaOrdersAuthStatus.Query
  ) {
    return true;
  }

  return false;
};

/**
 * Resolves status-update modal reason options for an auth-status transition.
 *
 * Query and denial options come from their organization-specific Firestore
 * slices, while query-resolution options retain the existing static list.
 *
 * @param oldValue - Auth status before the update.
 * @param newValue - Auth status selected by the user.
 * @param queryReasons - Firestore-configured query reasons.
 * @param denialReasons - Firestore-configured denial reasons.
 * @returns Label-value reason options appropriate for the status transition.
 */
export const getDropDownOptions = (
  oldValue: string | undefined,
  newValue: string | undefined,
  queryReasons: ReasonModel[],
  denialReasons: ReasonModel[],
): ReasonDropdownOption[] => {
  if (newValue === MedicalPaOrdersAuthStatus.Query) {
    return formatReasonDropdownOptions(queryReasons);
  }
  if (newValue === MedicalPaOrdersAuthStatus.DeniedByRISA) {
    return formatReasonDropdownOptions(denialReasons);
  }
  if (oldValue === MedicalPaOrdersAuthStatus.Query) {
    return QueryResolutionDropDownReasons;
  }
  return [];
};

export const getDisplayTextForStatusUpdateModal = (newValue: any) => {
  if (newValue === MedicalPaOrdersAuthStatus.Query) {
    return {
      label: "Reason for Query",
      placeholder: "Select the Reason for Query",
    };
  }
  if (newValue === MedicalPaOrdersAuthStatus.DeniedByRISA) {
    return {
      label: "Reason for Denial by RISA",
      placeholder: "Select the Reason for Denial by RISA",
    };
  }
  return {
    label: "Reason for Query Resolution",
    placeholder: "Select the Reason for Query Resolution",
  };
};

export const getCommentTypeForStatusUpdateModal = (newValue: any) => {
  if (newValue === MedicalPaOrdersAuthStatus.Query) {
    return "query";
  }
  if (newValue === MedicalPaOrdersAuthStatus.DeniedByRISA) {
    return "denial";
  }
  return "query_resolution";
};

/**
 * Opens the Hold modal after a Query resolution when the new status is Hold.
 *
 * This function handles a specific transition flow: when a user resolves a Query
 * and sets the status to Hold, the StatusUpdateModal is shown first (for query resolution),
 * and then this function triggers the Hold modal to capture hold-specific information.
 *
 * @param oldValue - The previous PA required state containing the original status
 * @param newValue - The new PA required state containing the updated status
 * @param dispatch - Redux dispatch function to trigger the modal opening action
 */
export const openHoldModalAfterQueryResolution = (
  oldValue: PaRequired,
  newValue: PaRequired,
  dispatch: AppDispatch,
) => {
  if (
    oldValue?.currentStatusId === MedicalPaOrdersAuthStatus.Query &&
    newValue?.currentStatusId === MedicalPaOrdersAuthStatus.Hold
  ) {
    dispatch(
      setOpenedModalId({
        id: ModalId.STATUS_CHANGED_TO_HOLD_MODAL,
      }),
    );
  }
};
